import React from 'react'
import PropTypes from 'prop-types'
import turfBbox from '@turf/bbox'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import bbox from '@turf/bbox'
import booleanOverlap from '@turf/boolean-overlap'
import booleanWithin from '@turf/boolean-within'
import { connect } from 'react-redux'
import { setMode } from '../../actions/mapSettings'
import { queryParcels } from './utils'

class ShapeSelect extends React.Component {
    static propTypes = {
        selectParcels: PropTypes.func.isRequired,
        deselectParcels: PropTypes.func.isRequired,
        selectedParcels: PropTypes.arrayOf(PropTypes.string).isRequired,
        map: PropTypes.shape({
            removeControl: PropTypes.func.isRequired,
            addControl: PropTypes.func.isRequired,
            on: PropTypes.func.isRequired,
            off: PropTypes.func.isRequired,
            project: PropTypes.func.isRequired
        }).isRequired
    }

    constructor(props) {
        super(props)
        this.altMode = false

        this.createDraw()
    }

    componentWillUnmount() {
        this.removeDraw()
    }

    createDraw = () => {
        this.draw = new MapboxDraw({ displayControlsDefault: false })
        this.props.map.addControl(this.draw)
        this.props.map.on('draw.create', this.finishDraw)

        this.draw.changeMode('draw_polygon')

        document.addEventListener('keydown', this.onKeyDown, true)
        document.addEventListener('keyup', this.onKeyUp, true)
    }

    removeDraw = () => {
        this.props.map.removeControl(this.draw)
        this.props.map.off('draw.create', this.finishDraw)
        this.draw = undefined

        document.removeEventListener('keydown', this.onKeyDown, true)
        document.removeEventListener('keyup', this.onKeyUp, true)
    }

    resetDraw = () => {
        this.removeDraw()
        this.createDraw()
    }

    onKeyDown = e => {
        if (e.code === 'Escape') {
            setTimeout(() => {
                this.draw.changeMode('draw_polygon')
            }, 200)
        } else if (e.key === 'Alt') {
            this.altMode = true
        }
    }

    onKeyUp = e => {
        if (e.key === 'Alt') {
            this.altMode = false
        }
    }

    finishDraw = () => {
        const data = this.draw.getAll()
        const updateSelection = new Promise(resolve => {
            if (data.features.length > 0) {
                const drawGeometry = data.features[0].geometry
                const drawBbox = bbox(drawGeometry)

                // project bbox to screen coordinates for the subsequent query
                const screenBbox = [
                    this.props.map.project([drawBbox[0], drawBbox[1]]),
                    this.props.map.project([drawBbox[2], drawBbox[3]])
                ]

                const bboxFeatures = queryParcels(this.props.map, screenBbox, this.props.selectedParcels)

                const filteredBboxFeatures = bboxFeatures.filter(feature => {
                    const { geometry } = feature
                    const intersects = geom => booleanWithin(geom, drawGeometry) || booleanOverlap(geom, drawGeometry)

                    if (geometry.type === 'MultiPolygon') {
                        const geoms = geometry.coordinates.map(coordinates => ({
                            type: 'Polygon',
                            coordinates
                        }))
                        if (!geoms.some(geom => intersects(geom))) {
                            return false
                        }
                    } else if (geometry.type !== 'Polygon' || !intersects(geometry)) {
                        return false
                    }

                    return true
                })

                if (this.altMode) {
                    // subtractive select
                    resolve(
                        this.props.deselectParcels(
                            filteredBboxFeatures
                                // filter out currently unselected features
                                .filter(feature => this.props.selectedParcels.includes(feature.properties.parcel_apn))
                                .map(feature => feature.properties.parcel_apn)
                        )
                    )
                } else {
                    // additive select
                    resolve(
                        this.props.selectParcels(
                            filteredBboxFeatures
                                // filter out currently selected features
                                .filter(feature => !this.props.selectedParcels.includes(feature.properties.parcel_apn))
                                .map(feature => {
                                    const { properties } = feature
                                    return {
                                        id: properties.parcel_id,
                                        apn: properties.parcel_apn,
                                        county: properties.countyname.toLowerCase(),
                                        address: {
                                            address: properties.site_addr,
                                            city: properties.site_city,
                                            zip: properties.site_zip,
                                            state: 'CA',
                                            label: `${properties.site_addr}, ${properties.site_city} ${properties.site_zip}`
                                        },
                                        bounds: turfBbox(feature),
                                        mpo: properties.mpo,
                                        specific_plan: properties.specific_plan,
                                        census_block: properties.census_block.split(' ').pop(),
                                        zoning_designation: JSON.parse(properties.zoning_designation)
                                    }
                                })
                        )
                    )
                }
            }
        })

        updateSelection.then(this.resetDraw)
    }

    render() {
        return null
    }
}

export default connect(
    ({ parcels: { items } }) => ({
        selectedParcels: items.filter(item => item.type === 'parcel').map(item => item.apn)
    }),
    dispatch => {
        return { setMode: mode => dispatch(setMode(mode)) }
    }
)(ShapeSelect)
